import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { apiService } from '../services/apiService';
import notificationService from '../CommanService/notificationService';
import signalRService from '../services/signalRService';
import Loader from '../CommanService/Loader';
import './BatchPricingPage.css';
import powerButton from './../assets/Icon/power_button.svg';
import resetIcon from './../assets/Icon/reset_icon.svg';
import warnning from './../assets/Icon/Warnning.svg';
import limit from './../assets/Icon/ban-svgrepo-com 1.svg'
import sliderIcon from './../assets/Icon/slider_icon.svg';


const BatchPricingPage = () => {
  const { user } = useSelector(state => state.auth);
  const [jobStatus, setJobStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isApplyingLimits, setIsApplyingLimits] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [results, setResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const prevStatusRef = useRef(null);

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      // Parse the date string
      const date = new Date(dateTimeString);
      
      // Format to show the exact time as stored in DB without timezone conversion
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth() + 1; // getUTCMonth() returns 0-11
      const day = date.getUTCDate();
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      const seconds = date.getUTCSeconds();
      
      // Convert to 12-hour format
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12; // Convert 0 to 12
      
      return `${month}/${day}/${year}, ${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ${ampm}`;
    } catch (error) {
      console.error('Error formatting date:', error, 'Input:', dateTimeString);
      return '-';
    }
  };

  const calculateTimeTaken = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return '-';
    
    try {
      const startTime = new Date(startedAt);
      const endTime = new Date(completedAt);
      const diffMs = endTime.getTime() - startTime.getTime();
      
      if (diffMs < 0) return '-';
      
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      
      const remainingMinutes = diffMinutes % 60;
      const remainingSeconds = diffSeconds % 60;
      
      if (diffHours > 0) {
        return `${diffHours}h ${remainingMinutes}m ${remainingSeconds}s`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes}m ${remainingSeconds}s`;
      } else {
        return `${remainingSeconds}s`;
      }
    } catch (error) {
      console.error('Error calculating time taken:', error);
      return '-';
    }
  };

  const isJobInProgress = () => {
    return jobStatus?.status === 'running' || jobStatus?.status === 'applying_limits';
  };

  // Load job status on component mount
  useEffect(() => {
    loadJobStatus();
    
    // Set up SignalR listener for real-time updates
    const handleJobStatusChange = (event) => {
      const newJobStatus = event.detail;
      setJobStatus(prevStatus => {
        // Preserve existing data when updating
        if (prevStatus && newJobStatus) {
          return {
            ...prevStatus,
            ...newJobStatus,
            // Preserve currentUserName if it exists in previous status and new status doesn't have it
            currentUserName: newJobStatus.currentUserName || prevStatus.currentUserName,
            // Preserve started and completed if they exist in previous status and new status doesn't have them
            started: newJobStatus.started || prevStatus.started,
            completed: newJobStatus.completed || prevStatus.completed
          };
        }
        return newJobStatus;
      });
      console.log('Job status updated via SignalR:', newJobStatus);
    };

    window.addEventListener('jobStatusChanged', handleJobStatusChange);

    return () => {
      window.removeEventListener('jobStatusChanged', handleJobStatusChange);
    };
  }, []);

  // Load results when job is completed
  useEffect(() => {
    if (jobStatus?.status === 'ready_to_run' || jobStatus?.status === 'completed') {
      loadResults();
    }
  }, [jobStatus?.status, currentPage]);

  // Track status changes for other functionality
  useEffect(() => {
    prevStatusRef.current = jobStatus?.status;
  }, [jobStatus?.status]);


  const loadJobStatus = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.GetJobStatus();
      if (response && response.data) {
        console.log('Job Status Data:', response.data);
        setJobStatus(response.data);
      }
    } catch (error) {
      console.error('Error loading job status:', error);
      notificationService.showError('Failed to load job status');
    } finally {
      setIsLoading(false);
    }
  };

  const loadResults = async () => {
    try {
      const response = await apiService.GetBatchPricingResults(currentPage, pageSize);
      if (response && response.data) {
        setResults(response.data);
        // Calculate total pages based on response
        // Note: You might need to modify the API to return total count
        setTotalPages(Math.ceil(response.data.length / pageSize));
      }
    } catch (error) {
      console.error('Error loading results:', error);
      notificationService.showError('Failed to load batch pricing results');
    }
  };

  const handleStartBatchPricing = async () => {
    try {
      setIsStarting(true);
      
      // // Hardcoded user name as requested
      // const currentUserId = "1"; // Keep a default user ID
       // const currentUserName = "Batch Pricing Routine";
       const suffix = "BatchPricing";
       // build name safely: join only non-empty values
       const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
      const currentUserId = String(user.id);
      const currentUserName = `${fullName}_${suffix}`;
      
      console.log('Sending user data:', { userId: currentUserId, userName: currentUserName });
      
      const response = await apiService.StartBatchPricing(currentUserId, currentUserName);
      
      if (response && response.data) {
        await loadJobStatus(); // Refresh status
      } else {
        notificationService.showError(response?.message || 'Failed to start batch pricing job');
      }
    } catch (error) {
      console.error('Error starting batch pricing:', error);
      notificationService.showError('Failed to start batch pricing job');
    } finally {
      setIsStarting(false);
    }
  };

  const handleResetJob = () => {
    setShowResetModal(true);
  };

  const confirmReset = async () => {
    setShowResetModal(false);
    
    try {
      setIsResetting(true);
      const response = await apiService.ResetJobStatus();
      
      if (response && response.data) {
        notificationService.showSuccess('Batch pricing Data deleted');
        setJobStatus(null);
        setResults([]);
        await loadJobStatus(); // Refresh status
      } else {
        notificationService.showError(response?.message || 'Failed to reset job status');
      }
    } catch (error) {
      console.error('Error resetting job status:', error);
      notificationService.showError('Failed to reset job status');
    } finally {
      setIsResetting(false);
    }
  };

  const cancelReset = () => {
    setShowResetModal(false);
  };

  const handleApplyLimits = async () => {
    try {
      setIsApplyingLimits(true);
      
      // Get current user name
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
      const currentUserName = `${fullName}_ApplyLimits`;
      
      console.log('Sending user data for ApplyLimits:', { userName: currentUserName });
      
      const response = await apiService.ApplyLimitsOnBatchPricing(currentUserName);
      
      if (response && response.data) {
        notificationService.showSuccess('Limits applied successfully');
        await loadJobStatus(); // Refresh status
      } else {
        notificationService.showError(response?.message || 'Failed to apply limits');
      }
    } catch (error) {
      console.error('Error applying limits:', error);
      notificationService.showError('Failed to apply limits');
    } finally {
      setIsApplyingLimits(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };


  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'running':
        return 'badge bg-warning text-dark';
      case 'applying_limits':
        return 'badge bg-info text-white';
      case 'ready_to_run':
        return 'badge bg-success';
      case 'failed':
        return 'badge bg-danger';
      case 'completed':
        return 'badge bg-primary';
      default:
        return 'badge bg-secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'running':
        return 'Running';
      case 'applying_limits':
        return 'Applying Limits';
      case 'ready_to_run':
        return 'Ready';
      case 'failed':
        return 'Failed';
      case 'completed':
        return 'Completed';
      default:
        return 'Unknown';
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="container-fluid">
      <div className="">
        <div className="">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              {/* <h4 className="mb-0">Batch Pricing Management</h4> */}
              <div className="d-flex w-100 justify-content-between">
                <div className="d-flex">
                <button
                  className="batch_prince_start_btn d-flex align-items-center"
                  onClick={handleStartBatchPricing}
                  disabled={isStarting || isApplyingLimits || jobStatus?.status === 'running' || jobStatus?.status === 'applying_limits'}
                >
                  <img src={powerButton} alt="Aesop Pricing Logo" className="mr_5"/> 
                  {isStarting ? (
                    <>
                      <span className="spinner-border" role="status" aria-hidden="true"></span>
                      Initializing...
                    </>
                  ) : jobStatus?.status === 'running' ? (
                    'Running...'
                  ) : (
                    <span className='font_16 fw-med mtn_2'>Start</span>
                  )}
                </button>
                <button
                  className="reset_prince_start_btn d-flex align-items-center"
                  onClick={handleApplyLimits}
                  disabled={isApplyingLimits || isStarting || jobStatus?.status === 'running' || jobStatus?.status === 'applying_limits'}
                >
                  <img src={limit} alt="Apply Limits"  className="mr_5"/>
                  {isApplyingLimits ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Applying...
                    </>
                  ) : (
                    <span className='font_16 fw-med mtn_2'>Apply Limits</span>
                  )}
                </button>
                </div>
                <button
                  className="reset_prince_start_btn d-flex align-items-center"
                  onClick={handleResetJob}
                  disabled={isResetting}
                >
                  <img src={resetIcon} alt="Reset"  className="mr_5"/>
                  {isResetting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Resetting...
                    </>
                  ) : (
                    'Reset'
                  )}
                </button>
              </div>
            </div>

            <div className="card-body position-relative">
              {/* Job Status Section */}
              <div className="row batch_row">
                <div className="col-md-12 pb_0">
                  {/* <h5>Job Status</h5> */}
                  {jobStatus ? (
                    <div className="card batch_status_row">
                        <div className="row">
                          {/* First Row: Status, Progress, Current User */}
                          <div className="col-md-4">
                            <h6 className="font_18 fw-semi text_black">Status:</h6>
                            <span className={`font_18 fw-reg text_black p-0 ${getStatusBadgeClass(jobStatus.status)}`}>
                              {getStatusText(jobStatus.status)}
                            </span>
                          </div>
                          <div className="col-md-4">
                            <h6 className="font_18 fw-semi text_black">Progress:</h6>
                            <span className="font_18 fw-reg text_black p-0">
                              {jobStatus.processedCombinations || 0} / {jobStatus.totalCombinations || 0}
                              ({Math.round(jobStatus.progressPercentage || 0)}%)
                            </span>
                          </div>
                          <div className="col-md-4">
                            <h6 className="font_18 fw-semi text_black">Current User:</h6>
                            <span className="font_18 fw-reg text_black p-0">
                              {jobStatus.currentUserName?.split('_')[0] || jobStatus.currentUserName || 'N/A'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="row mt-3">
                          {/* Second Row: Started At, Completed At, Time Taken */}
                          <div className="col-md-4">
                            <h6 className="font_18 fw-semi text_black">Started At:</h6>
                            <span className="font_18 fw-reg text_black p-0">
                              {formatDateTime(jobStatus.started)}
                            </span>
                          </div>
                          <div className="col-md-4">
                            <h6 className="font_18 fw-semi text_black">Completed At:</h6>
                            <span className="font_18 fw-reg text_black p-0">
                              {isJobInProgress() ? 'N/A' : formatDateTime(jobStatus.completed)}
                            </span>
                          </div>
                          <div className="col-md-4">
                            <h6 className="font_18 fw-semi text_black">Time Taken:</h6>
                            <span className="font_18 fw-reg text_black p-0">
                              {jobStatus?.status === 'ready_to_run' ? calculateTimeTaken(jobStatus.started, jobStatus.completed) : 'N/A'}
                            </span>
                          </div>
                        </div>
                        {jobStatus.errorMessage && (
                          <div className="row mt-2">
                            <div className="col-12">
                              <div className="alert alert-danger">
                                <strong>Error:</strong> {jobStatus.errorMessage}
                              </div>
                            </div>
                          </div>
                        )}
                        {(jobStatus.status === 'running' || jobStatus.status === 'applying_limits') && (
                          <div className="row mt-2">
                            <div className="col-12 p-0">
                              <div className="progress batch_progress_bar">
                                <div 
                                  className="progress-bar progress-bar-striped progress-bar-animated" 
                                  role="progressbar" 
                                  style={{ width: `${Math.round(jobStatus.progressPercentage || 0)}%` }}
                                  aria-valuenow={Math.round(jobStatus.progressPercentage || 0)}
                                  aria-valuemin="0" 
                                  aria-valuemax="100"
                                >
                                  {Math.round(jobStatus.progressPercentage || 0)}%
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  ) : (
                    <div className="alert alert-info">
                      No job status available. Click "Start Batch Pricing" to begin.
                    </div>
                  )}
                </div>
              </div>

              {/* Results Section - Hidden */}
            </div>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="modal-backdrop">
          <div className="add-user-modal w_500px">
            <button className="modal-close" onClick={cancelReset}>✖</button>
            
              <div className='custom_confirm_rst'>
                <img src={warnning} className='mb_15 '></img>
                <p className="mb-0 text-muted mb_15 font_20 fw-reg">This action will truncate all finalized data and cannot be undone.</p>
                <h4 className='mb_15 fw-semi font_20 fw-semi'> Are you sure you want to continue?</h4>
                <button type="button" onClick={cancelReset} className='mr_30 font_14 fw-semi cancel_btn'>Cancel</button>
                <button 
                className=' reset_btn font_14 fw-reg'
                type="button" 
                onClick={confirmReset}
                disabled={isResetting}

              >
                {isResetting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Resetting...
                  </>
                ) : (
                  'Reset'
                )}
              </button>
              </div>
            {/* <div className="modal-actions">
              
              
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchPricingPage;
